import { useMemo, useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/site';
import { useSeo } from '@/hooks/useSeo';

export default function TopicsPage() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get('cat') || 'all';
  const [keyword, setKeyword] = useState(params.get('q') || '');

  useEffect(() => {
    setKeyword(params.get('q') || '');
  }, [params]);

  useSeo({
    title: '专题合集总览 - 黑料社网分类合集与盘点导航',
    description:
      '黑料社网专题合集总览页，按热搜盘点、娱乐观察、网络话题、影像现场、文化盘点五个分类整理全部合集，支持关键词搜索，快速定位想看的专题。',
    keywords: '专题合集,合集总览,分类导航,黑料社网专题,精选盘点',
    canonicalPath: '/topics',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '专题合集总览',
        url: 'https://jinpenge.cn/topics',
        description: '按分类整理的全部专题合集与盘点合集导航。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jinpenge.cn/' },
          { '@type': 'ListItem', position: 2, name: '专题合集', item: 'https://jinpenge.cn/topics' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '全部专题合集',
        itemListElement: topics.map((t, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: t.title,
          url: `https://jinpenge.cn/topic/${t.slug}`,
        })),
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return topics.filter((t) => {
      const matchCat = activeCat === 'all' || t.category === activeCat;
      const matchKw =
        !kw ||
        t.title.toLowerCase().includes(kw) ||
        t.summary.toLowerCase().includes(kw) ||
        t.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, keyword]);

  const switchCat = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'all') next.delete('cat');
    else next.set('cat', key);
    setParams(next, { replace: true });
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (keyword.trim()) next.set('q', keyword.trim());
    else next.delete('q');
    setParams(next, { replace: true });
  };

  return (
    <div className="pb-4">
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-10 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '专题合集' }]} />
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950">专题合集总览</h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            这里收录了黑料社网全部上线中的专题合集与盘点合集。按分类筛选，或用关键词搜索，找到想看的主题后一次看完。
          </p>
        </div>
      </div>

      <div className="px-4 md:px-10 py-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 lg:gap-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = activeCat === cat.key;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => switchCat(cat.key)}
                  className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </button>
              );
            })}
          </div>

          <form onSubmit={submitSearch} className="flex items-center gap-2 bg-background-100 border border-background-300 rounded-md px-3 h-11 w-full lg:w-80">
            <i className="ri-search-line text-foreground-500 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索专题标题或标签"
              className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
            />
            <button type="submit" className="text-xs text-primary-600 font-medium cursor-pointer whitespace-nowrap">
              搜索
            </button>
          </form>
        </div>

        <div className="mt-6 flex items-center justify-between text-xs text-foreground-500">
          <span>
            共 <strong className="text-foreground-800 font-medium">{filtered.length}</strong> 个专题
          </span>
          {(activeCat !== 'all' || keyword) && (
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          )}
        </div>
      </div>

      <div className="px-4 md:px-10 pb-10">
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300"></i>
            <p className="mt-4 font-heading text-lg text-foreground-800">没有找到匹配的专题</p>
            <p className="mt-2 text-sm text-foreground-500">试试换一个关键词，或者切换到「全部专题」看看。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {filtered.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}